import { memo, useCallback, useEffect, useMemo, useState } from "react";
import * as Bananas from "bananas-commerce-admin";

import AddIcon from "@mui/icons-material/Add";
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";

import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";
import { omit } from "typedash/omit";

import LabeledSpinner from "@/components/LabeledSpinner";
import Select, { ReactNodeOption } from "@/components/Select";

import { Case } from "@/extensions/klarahill/types";
import { PublicationAbbreviation } from "@/extensions/mediahouse/types";
import * as story from "@/extensions/obituary/pages/detail/Story";
import { Story } from "@/extensions/obituary/pages/detail/Story";
import * as convert from "@/extensions/obituary/pages/detail/Story/convert";
import useDeadlinesQuery from "@/extensions/obituary/queries/useDeadlinesQuery";
import usePublicationsQuery from "@/extensions/obituary/queries/usePublicationsQuery";
import { Ad, CreateAdPayload, Layout } from "@/extensions/obituary/types";
import adName from "@/extensions/obituary/utils/adName";

import useSettings from "@/hooks/useSettings";

import { Nullish, Option } from "@/utils/types";

interface Settings {
  layout: Nullish<Layout>;
  publication: Nullish<PublicationAbbreviation>;
  publicationDate: Nullish<Dayjs>;
  editingDeadline: Nullish<Dayjs>;
  story: Nullish<Story>;
}

interface Template extends Omit<Option, "value"> {
  value: Story;
  layout_variant: Layout;
}

export interface CreateAdProps {
  ads: Ad[];
  c: Case;
  onSubmit: (adResponse: Ad) => void;
  onClose?: () => void;
}

const CreateAd: React.FC<CreateAdProps> = ({ ads, c, onClose, onSubmit }) => {
  const api = Bananas.useApi();
  const { enqueueSnackbar } = useSnackbar();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { settings, setSettings } = useSettings<Settings>({
    layout: undefined,
    publication: undefined,
    publicationDate: undefined,
    editingDeadline: undefined,
    story: undefined,
  });

  const publications = usePublicationsQuery(settings.publication);
  const deadlines = useDeadlinesQuery(settings.publication, settings.layout);

  const canSubmit = useMemo(() => {
    const withoutDate = omit(settings, ["editingDeadline"]);
    return Object.values(withoutDate).every(Boolean);
  }, [settings]);

  const adTemplates = useMemo(
    () =>
      ads.map((ad) => ({
        label: adName(ad),
        value: ad.story,
        layout_variant: ad.layout_variant,
      })),
    [c, ads],
  );

  const defaultTemplates = useMemo(() => {
    const layouts: Layout[] = [
      "OBITUARY_ONE_COLUMN",
      "OBITUARY_TWO_COLUMN",
      "THANKS",
    ];

    const result: Record<Layout, Template> = {} as Record<Layout, Template>;

    for (const layout of layouts) {
      const defaultTemplate = story.template[layout];
      result[layout] = {
        label: "Data från begravningsärendet",
        value: defaultTemplate(c),
        layout_variant: layout,
      };
    }

    return result;
  }, []);

  const templates = useMemo(() => {
    const templates = adTemplates.filter((t) => {
      if (settings.layout === "THANKS") {
        if (t.layout_variant !== "THANKS") return false;
      } else if (t.layout_variant === "THANKS") return false;

      return true;
    });

    if (settings.layout) templates.push(defaultTemplates[settings.layout]);

    return templates;
  }, [adTemplates, settings.layout]);

  const selectedTemplateLayout = useMemo(
    () =>
      templates.find((template) => template.value === settings.story)
        ?.layout_variant,
    [templates, settings.story],
  );

  // Set preselected story. Defaults to case data (if no ads) or the latest ad (if exists).
  useEffect(() => {
    const preselectedStory = templates[0]?.value;
    if (
      (settings.story == null && preselectedStory != null) ||
      !templates.some((t) => t.value === settings.story)
    ) {
      setSettings({ story: preselectedStory });
    }
  }, [templates, settings.layout]);

  /// Callbacks

  const handleGetOptionLabel = useCallback(
    ({ disabled, label }: Option) =>
      disabled ? `${label} (ej tillgänglig)` : label,
    [],
  );

  const handleGetOptionDisabled = useCallback(
    ({ disabled }: Option) => disabled ?? true,
    [],
  );

  const handleSelectPublication = useCallback(
    (_: React.SyntheticEvent<Element, Event>, option: Nullish<Option>) => {
      if (option == null) {
        enqueueSnackbar("Invalid publication.", { variant: "error" });
        return;
      }

      if (publications.data == null) return;

      const publication = option.value as PublicationAbbreviation;
      if (!publications.data.some((p) => p.abbreviation === publication)) {
        enqueueSnackbar("Ogiltig publikation.", { variant: "error" });
      }

      setSettings({ publication });
    },
    [publications],
  );

  const [hoveredDate, setHoveredDate] = useState<Nullish<Dayjs>>(null);
  const isDeadlinePassed = useMemo(() => {
    if (settings.editingDeadline == null) {
      return false;
    }
    return settings.editingDeadline.isBefore(dayjs());
  }, [settings.editingDeadline]);

  const handleSelectDate = useCallback(
    (newDate: Nullish<Dayjs>) => {
      if (newDate == null) return;

      const newDeadline = deadlines.data?.find((d) =>
        newDate.isSame(d.publicationDate),
      );

      if (newDeadline == null) {
        enqueueSnackbar("Det går inte att välja detta datum.", {
          variant: "error",
        });
        return;
      }

      setSettings({
        publicationDate: newDeadline.publicationDate,
        editingDeadline: newDeadline.deadlineTime,
      });
    },
    [deadlines.data],
  );

  /** Map of publication dates -> deadline because we
   * want to highlight the deadline date on the date picker. */
  const publicationToDeadlineMap = useMemo<Map<number, number>>(() => {
    if (deadlines?.data == null) return new Map();
    const map = new Map<number, number>();
    for (const deadline of deadlines.data) {
      const publicationKey = deadline.publicationDate.startOf("day").valueOf();
      const deadlineKey = deadline.deadlineTime.startOf("day").valueOf();
      map.set(publicationKey, deadlineKey);
    }
    return map;
  }, [deadlines.data]);

  /** Map of publication dates -> deadline times for tooltip deadlijne time lookups. */
  const deadlineTimes = useMemo<Map<number, string>>(() => {
    if (deadlines?.data == null) return new Map();
    const map = new Map<number, string>();
    for (const deadline of deadlines.data) {
      const key = deadline.publicationDate.startOf("day").valueOf();
      const time = deadline.deadlineTime.format("YYYY-MM-DD HH:mm");
      map.set(key, time);
    }
    return map;
  }, [deadlines.data]);

  const renderDayTooltip = useCallback(
    (pickersDayProps: PickersDayProps<Dayjs>) => {
      const dayKey = pickersDayProps.day.startOf("day").valueOf();
      const deadlineTime = deadlineTimes.get(dayKey);

      const isDeadlineDate =
        hoveredDate != null &&
        publicationToDeadlineMap.get(hoveredDate.startOf("day").valueOf()) ===
          dayKey;

      const deadline =
        hoveredDate != null
          ? deadlines.data?.find(
              (d) =>
                d.publicationDate.startOf("day").valueOf() ===
                hoveredDate.startOf("day").valueOf(),
            )
          : null;
      const isDeadlinePast =
        deadline != null && deadline.deadlineTime.isBefore(dayjs());

      const dayComponent = (
        <PickersDay
          {...pickersDayProps}
          sx={{
            ...(isDeadlineDate && {
              border: "2px solid",
              borderColor: isDeadlinePast ? "warning.main" : "primary.main",
              bgcolor: "background.paper",
            }),
          }}
          onMouseEnter={() => setHoveredDate(pickersDayProps.day)}
          onMouseLeave={() => setHoveredDate(null)}
        />
      );

      if (deadlineTime == null) {
        return dayComponent;
      }

      return (
        <Tooltip arrow title={`Skickas in senast: ${deadlineTime}`}>
          {dayComponent}
        </Tooltip>
      );
    },
    [deadlineTimes, hoveredDate, publicationToDeadlineMap, deadlines.data],
  );

  const handleSubmit = useCallback(async () => {
    if (hasSubmitted) return;
    setHasSubmitted(true);

    if (!canSubmit) {
      enqueueSnackbar("Fyll i alla fält.", { variant: "error" });
      setHasSubmitted(false);
      return;
    }

    const action = api.operations["obituary.ad:create"];
    if (action == null) {
      setHasSubmitted(false);
      throw new Error('Invalid action "obituary.ad:create"');
    }

    let story = settings.story!;
    // Convert two-column templates to one-column layouts
    if (
      settings.layout === "OBITUARY_ONE_COLUMN" &&
      selectedTemplateLayout === "OBITUARY_TWO_COLUMN"
    ) {
      story = convert.twoColumnToOneColumn(story);
    }

    const payload: CreateAdPayload = {
      case_id: c.id,
      document: {
        story,
        layout: settings.layout!,
        publication: settings.publication!,
      },
    };

    if (settings.publicationDate != null && settings.editingDeadline != null) {
      payload.deadline = {
        publication_date: settings.publicationDate.format("YYYY-MM-DD"),
        editing_deadline: settings.editingDeadline.toISOString(),
      };
    }

    const response = await action.call({ body: payload });
    if (response.ok) {
      const data = (await response.json()) as Ad;
      onSubmit(data);
      setHasSubmitted(false);
    } else {
      enqueueSnackbar(
        "Fel vid skapande av annons. Försök igen eller kontakta support om felet kvarstår.",
        { variant: "error" },
      );
      setHasSubmitted(false);
    }
  }, [c, settings]);

  return !publications.hasAllData ? (
    <LabeledSpinner
      label="Hämtar publikationer..."
      sx={{ minWidth: 550, minHeight: 440 }}
    />
  ) : (
    <Box
      component="article"
      sx={{ position: "relative", minWidth: 550, minHeight: 370 }}
    >
      {hasSubmitted && <LabeledSpinner backdrop label="Skapar annons..." />}

      <DialogTitle>Skapa en ny annons</DialogTitle>

      <DialogContent>
        <Stack gap={2} pt={1}>
          <Autocomplete
            disablePortal
            fullWidth
            getOptionDisabled={handleGetOptionDisabled}
            getOptionLabel={handleGetOptionLabel}
            ListboxProps={{ sx: { maxHeight: 240 } }}
            options={publications.options ?? []}
            renderInput={(params) => (
              <TextField {...params} label="Publikation" />
            )}
            onChange={handleSelectPublication}
          />

          <Select
            disabled={settings.publication == null}
            label="Mall"
            options={publications.layoutOptions ?? []}
            value={settings.layout ?? ""}
            onChange={({ target }) =>
              setSettings({ layout: target.value as Layout })
            }
          />

          {templates.length > 1 && (
            <Select
              disabled={settings.layout == null}
              label="Utgå från"
              options={templates as unknown as ReactNodeOption[]}
              value={settings.story ?? ""}
              onChange={({ target }) =>
                setSettings({ story: target.value as Story })
              }
            />
          )}

          <FormControl fullWidth>
            <DatePicker
              defaultValue={
                settings.publicationDate ?? deadlines.firstAvaliableDate
              }
              format="YYYY-MM-DD"
              label="Införandedatum"
              shouldDisableDate={deadlines.handleShouldDisableDate}
              slotProps={{
                textField: {
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: isDeadlinePassed
                          ? "warning.main"
                          : undefined,
                      },
                    },
                  },
                },
              }}
              slots={{
                day: renderDayTooltip,
              }}
              value={settings.publicationDate}
              onChange={handleSelectDate}
            />
            <FormHelperText
              sx={{
                color: isDeadlinePassed ? "warning.main" : undefined,
              }}
            >
              {isDeadlinePassed
                ? "Deadline för redigering har passerat."
                : "Det går enbart att välja lediga införandedatum. Du kan ändra datum innan du skickar in."}
            </FormHelperText>
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions>
        {onClose && <Button onClick={onClose}>Avbryt</Button>}
        <Button
          disabled={hasSubmitted}
          startIcon={<AddIcon />}
          onClick={handleSubmit}
        >
          Skapa annons
        </Button>
      </DialogActions>
    </Box>
  );
};

export default memo(CreateAd);
